import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import CtaSection from '@/components/base/CtaSection';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
            .slice(0, 3)
        : [],
    [article]
  );

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 暗网吃瓜`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt);
      }
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <i className="ri-file-unknow-line text-foreground-600 text-5xl w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
            <h2 className="text-foreground-300 text-xl mb-2">文章不存在</h2>
            <Link to="/" className="text-primary-500 text-sm hover:text-primary-400 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.authorName,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '暗网吃瓜',
      url: 'http://cccontrol.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://cccontrol.cn/article/${slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://cccontrol.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `http://cccontrol.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const formattedDate = new Date(article.publishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.category, to: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <main>
          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 bg-primary-500/10 text-primary-400 text-xs font-medium rounded-full hover:bg-primary-500/20 transition-colors"
                >
                  {article.category}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-200 text-foreground-500 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 leading-tight mb-4 tracking-wide">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-foreground-500 text-sm mb-8 pb-6 border-b border-background-300/30">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full overflow-hidden">
                    <img
                      src={article.authorAvatar}
                      alt={article.authorName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-foreground-300">{article.authorName}</span>
                </div>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {formattedDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  阅读约{article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
              </div>

              <div className="relative rounded-2xl overflow-hidden h-[360px] md:h-[450px] mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 暗网吃瓜`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-invert max-w-none
                  prose-p:text-foreground-300 prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-100 prose-strong:font-semibold
                  prose-h2:text-foreground-100 prose-h2:text-xl prose-h2:font-heading prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-foreground-200 prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                  [&_p]:tracking-wide"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex items-center gap-4 mt-10 pt-6 border-t border-background-300/30">
                <span className="text-foreground-500 text-sm">分享到：</span>
                <a
                  href="#"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 hover:text-primary-500 hover:bg-background-300 transition-all cursor-pointer"
                  aria-label="分享到微博"
                >
                  <i className="ri-weibo-line text-lg w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 hover:text-accent-500 hover:bg-background-300 transition-all cursor-pointer"
                  aria-label="分享到微信"
                >
                  <i className="ri-wechat-line text-lg w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 hover:text-foreground-100 hover:bg-background-300 transition-all cursor-pointer"
                  aria-label="复制链接"
                >
                  <i className="ri-link text-lg w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-6xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-100 mb-8 tracking-wider">
                  <span className="text-primary-500">「 </span>
                  相关爆料
                  <span className="text-primary-500"> 」</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <CtaSection
            title="不想错过下一个大瓜？"
            subtitle="下载暗网吃瓜app，推送提醒快人一步"
            buttonText="立即下载暗网吃瓜APP"
          />
        </main>

        <Footer />
      </div>
    </>
  );
}